/**
 * 生成全局唯一的 traceId（16 字节，可转换为二进制）
 * 格式：32 位十六进制字符串
 */
export function generateId(): string {
  // 生成 16 字节的随机数据（128 位）
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // 降级方案：使用 Math.random
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // 转换为十六进制字符串
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * 生成简短的唯一 spanId（8 字节，可转换为二进制）
 * 格式：16 位十六进制字符串
 */
export function generateShortId(): string {
  // 生成 8 字节的随机数据（64 位）
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // 降级方案：使用 Math.random
    for (let i = 0; i < 8; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // 转换为十六进制字符串
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * 获取高精度时间戳（毫秒）
 */
export function getTimestamp(): number {
  // 优先使用 performance.now()，如果不可用则使用 Date.now()
  if (typeof performance !== 'undefined' && performance.now) {
    // performance.now() 返回的是相对于页面加载的时间，需要加上 performance.timing.navigationStart
    const navigationStart = performance.timing?.navigationStart || 0;
    return navigationStart + performance.now();
  }
  return Date.now();
}

/**
 * 将对象转换为字符串（用于日志）
 */
export function stringifyValue(value: any): string {
  if (value === null || value === undefined) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
}

/**
 * 检查 URL 是否有效
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * 将十六进制字符串转换为 Uint8Array（二进制格式）
 */
export function hexToBinary(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new Error('十六进制字符串长度必须为偶数');
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

/**
 * 将 Uint8Array 转换为十六进制字符串
 */
export function binaryToHex(bytes: Uint8Array): string {
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * 验证 traceId 格式（32 位十六进制）
 */
export function isValidTraceId(traceId: string): boolean {
  return /^[0-9a-f]{32}$/i.test(traceId);
}

/**
 * 验证 spanId 格式（16 位十六进制）
 */
export function isValidSpanId(spanId: string): boolean {
  return /^[0-9a-f]{16}$/i.test(spanId);
}
